import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  TableSortLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { fDateLine, fTimeLine } from '../../utils/formatTime';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
} from '../../store/users/usersSlice';
import {
  findUser,
  updateUserProfile,
  createUsers,
} from '../../store/users/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
// import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Searchbar from '../../components/Searchbar';
import { omit } from 'lodash';
import {
  reset as resetUsers,
  setStatus as setUsersStatus,
} from '../../store/users/usersSlice';
import userSchema from '../../validation/userSchema';
import UsersFormModal from './UsersFormModal';
import { Users } from '../../store/users/api/interfaces';
import UsersActionModal from './UserActionModal';
import LicenseManagementModal from './LicenseManagementModal';
import CompanyDetailsModal from './CompanyDetailsModal';
import RoleConversionModal from './RoleConversionModal';
import { SortBy, SuperUserRole } from '../../enum';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.user);
  const [search, setSearch] = useState<string>('');
  const [selectedUserRole, setSelectedUserRole] = useState<string>(
    searchParams.get('userRole') || ''
  );
  const [isApprovedFilter, setIsApprovedFilter] = useState<boolean | undefined>(
    searchParams.get('isApproved') !== null
      ? searchParams.get('isApproved') === 'true'
      : undefined
  );
  const [actionUser, setActionUser] = useState<Users>();
  const [sortBy, setSortBy] = useState(SortBy.createdAt);
  const [sortByOrder, setSortByOrder] = useState('DESC');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [excludeInactive, setExcludeInactive] = useState<boolean>(false);

  // Sync filters with URL params only on initial load or when URL changes
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only sync from URL when searchParams actually change (e.g., navigation)
    const urlUserRole = searchParams.get('userRole') || '';
    const urlIsApproved = searchParams.get('isApproved');
    const urlIsApprovedValue = urlIsApproved !== null
      ? urlIsApproved === 'true'
      : undefined;

    setSelectedUserRole(urlUserRole);
    setIsApprovedFilter(urlIsApprovedValue);
  }, [searchParams]);
  const { toggle, onOpen, onClose } = useToggle();
  const {
    toggle: toggleAction,
    onOpen: onOpenAction,
    onClose: onCloseAction,
  } = useToggle();
  const {
    toggle: toggleLicense,
    onOpen: onOpenLicense,
    onClose: onCloseLicense,
  } = useToggle();
  const {
    toggle: toggleDetails,
    onOpen: onOpenDetails,
    onClose: onCloseDetails,
  } = useToggle();
  const {
    toggle: toggleRoleConversion,
    onOpen: onOpenRoleConversion,
    onClose: onCloseRoleConversion,
  } = useToggle();
  const [roleConversionUser, setRoleConversionUser] = useState<Users>();

  // Track previous filter values to detect filter changes and prevent double API calls
  const prevFiltersRef = useRef({ search, selectedUserRole, isApprovedFilter, startDate, endDate, excludeInactive });
  const skipAfterResetRef = useRef(false);

  useEffect(() => {
    // Check if filters changed (not just skip/limit)
    const filtersChanged =
      prevFiltersRef.current.search !== search ||
      prevFiltersRef.current.selectedUserRole !== selectedUserRole ||
      prevFiltersRef.current.isApprovedFilter !== isApprovedFilter ||
      prevFiltersRef.current.startDate !== startDate ||
      prevFiltersRef.current.endDate !== endDate ||
      prevFiltersRef.current.excludeInactive !== excludeInactive;

    // Skip the second call after reset (when skip changes to 0)
    if (skipAfterResetRef.current && skip === 0 && !filtersChanged) {
      skipAfterResetRef.current = false;
      return;
    }

    if (filtersChanged) {
      // Reset pagination when filters change
      prevFiltersRef.current = { search, selectedUserRole, isApprovedFilter, startDate, endDate, excludeInactive };
      skipAfterResetRef.current = true;
      dispatch(resetUsers());
    }

    // Fetch users with current filter values
    dispatch(
      findUser({
        limit,
        skip: filtersChanged ? 0 : skip,
        ...(search && { search: search }),
        ...(selectedUserRole && { userRole: selectedUserRole }),
        ...(isApprovedFilter !== undefined && { isApproved: isApprovedFilter }),
        ...(sortBy && { sortByName: sortBy }),
        ...(sortByOrder && { sortByOrder: sortByOrder }),
        ...(startDate && { startDate: startDate.toISOString().split('T')[0] }),
        ...(endDate && { endDate: endDate.toISOString().split('T')[0] }),
        ...(excludeInactive && { excludeInactive: true }),
      })
    );
  }, [dispatch, limit, skip, search, selectedUserRole, isApprovedFilter, sortBy, sortByOrder, startDate, endDate, excludeInactive]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
    getFieldProps,
  } = useFormik({
    initialValues: {
      id: '',
      name: '',
      // firstName: '',
      // lastName: '',
      countryCallingCode: '+974',
      mobileNumber: '',
      email: '',
      password: '',
      language: 'en',
      userRole: '',
      activeState: true,
      freeAds: '0',
      maxAds: '0',
    },
    validationSchema: userSchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          const reqBody = {
            id: values?.id,
            name: values?.name,
            email: values?.email,
            countryCallingCode: values?.countryCallingCode,
            mobileNumber: values?.mobileNumber,
            activeState: values?.activeState,
            language: values?.language,
            freeAds: values?.freeAds ?? '0',
            maxAds: values?.maxAds ?? '0',
          };
          await dispatch(updateUserProfile(reqBody)).unwrap();
        }
        if (!values.id) {
          const reqBody = omit(values, ['id', 'activeState']);
          await dispatch(createUsers(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        dispatch(
          findUser({
            limit,
            skip,
            ...(search && { search: search }),
            ...(selectedUserRole && { userRole: selectedUserRole }),
            ...(isApprovedFilter !== undefined && { isApproved: isApprovedFilter }),
            ...(sortBy && { sortByName: sortBy }),
            ...(sortByOrder && { sortByOrder: sortByOrder }),
            ...(startDate && { startDate: startDate.toISOString().split('T')[0] }),
            ...(endDate && { endDate: endDate.toISOString().split('T')[0] }),
            ...(excludeInactive && { excludeInactive: true }),
          })
        );
      } catch (error: any) { }
    },
  });

  const handleUpdate = (value: Users): void => {
    onOpen();
    setValues({
      id: value?.id,
      name: value?.name,
      // firstName: value?.firstName,
      // lastName: value?.lastName,
      countryCallingCode: value?.countryCallingCode,
      mobileNumber: value?.mobileNumber,
      email: value?.email,
      password: value?.password,
      userRole: value?.userRole,
      activeState: value?.activeState,
      language: value?.language,
      freeAds: value?.freeAds ?? '0',
      maxAds: value?.maxAds ?? '0',
    });
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setUsersStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  const handleUserRoleChange = (event: any): void => {
    const newRole = event.target.value;
    setSelectedUserRole(newRole);
    // Reset to first page when filter changes
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  const handleActionUpdate = (value: Users): void => {
    setActionUser(value);
    onOpenAction();
  };

  const handleLicenseManagement = (user: Users): void => {
    setActionUser(user);
    onOpenLicense();
  };

  const handleRefreshUsers = () => {
    dispatch(
      findUser({
        limit,
        skip,
        ...(search && { search: search }),
        ...(selectedUserRole && { userRole: selectedUserRole }),
        ...(isApprovedFilter !== undefined && { isApproved: isApprovedFilter }),
        ...(sortBy && { sortByName: sortBy }),
        ...(sortByOrder && { sortByOrder: sortByOrder }),
        ...(startDate && { startDate: startDate.toISOString().split('T')[0] }),
        ...(endDate && { endDate: endDate.toISOString().split('T')[0] }),
        ...(excludeInactive && { excludeInactive: true }),
      })
    );
  };

  const handleActionModalClose = () => {
    onCloseAction();
    // Clear action user when modal closes
    setActionUser(undefined);
    // Refresh list when modal closes
    handleRefreshUsers();
  };

  const handleRequestSort = (property: any) => {
    const isAsc = sortBy === property && sortByOrder === 'ASC';
    setSortByOrder(isAsc ? 'DESC' : 'ASC');
    setSortBy(property);
  };

  // const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // // Function to toggle "View More" or "View Less"
  // const toggleExpand = (userId: string) => {
  //   setExpandedRows((prev) => ({
  //     ...prev,
  //     [userId]: !prev[userId],
  //   }));
  // };

  return (
    <>
      <Page title={'Users: List'}>
        <Container maxWidth={'xl'}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            spacing={{ xs: 0.5, sm: 1.5 }}
            my={1}
          >
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={onOpen}
            >
              {'New Admin'}
            </Button>
          </Stack>

          <Card>
            <Box sx={{ px: 2, pt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Searchbar handleSearch={handleSearch} compact />
                </Box>
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel id="user-role-filter-label">Filter by Role</InputLabel>
                  <Select
                    labelId="user-role-filter-label"
                    id="user-role-filter"
                    value={selectedUserRole}
                    label="Filter by Role"
                    onChange={handleUserRoleChange}
                  >
                    <MenuItem value="">All Roles</MenuItem>
                    <MenuItem value={SuperUserRole.admin}>Admin</MenuItem>
                    <MenuItem value={SuperUserRole.adminTier2}>Admin Tier 2</MenuItem> 
                    <MenuItem value={SuperUserRole.adminTier3}>Admin Tier 3</MenuItem>
                    <MenuItem value={SuperUserRole.individual}>Individual</MenuItem>
                    <MenuItem value={SuperUserRole.company}>Company</MenuItem>
                    <MenuItem value={SuperUserRole.agent}>Agent</MenuItem>
                    <MenuItem value={SuperUserRole.broker}>Broker</MenuItem>
                  </Select>
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(value) => setStartDate(value)}
                    renderInput={(params) => <TextField {...params} size="small" sx={{ minWidth: 150 }} />}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(value) => setEndDate(value)}
                    renderInput={(params) => <TextField {...params} size="small" sx={{ minWidth: 150 }} />}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1 }}>
                <FormControlLabel
                  sx={{ m: 0, mr: 0 }}
                  control={
                    <Checkbox
                      checked={excludeInactive}
                      onChange={(e) => {
                        setExcludeInactive(e.target.checked);
                        dispatch(setSkip({ skip: 0 }));
                        dispatch(setCurrentPage({ currentPage: 0 }));
                      }}
                      color="primary"
                      size="small"
                    />
                  }
                  label="Exclude Inactive Users"
                />
              </Box>
            </Box>

            <TableContainer sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
              <Table
                size={'medium'}
                sx={{
                  minWidth: 1200,
                  borderCollapse: 'separate',
                  borderSpacing: '0 10px',
                }}
              >
                <TableHead
                  sx={{
                    background: '#f2d2df',
                  }}
                >
                  <TableRow>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.name}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.name)}
                      >
                        <div style={{ color: '#64013c' }}>{'Name'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.email}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.email)}
                      >
                        <div style={{ color: '#64013c' }}> {'Email'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.mobileNumber}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.mobileNumber)}
                      >
                        <div style={{ color: '#64013c' }}>
                          {'Mobile Number'}
                        </div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center' sx={{ minWidth: 120 }}>
                      <TableSortLabel
                        active={sortBy === SortBy.createdAt}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.createdAt)}
                        sx={{
                          alignItems: 'center',
                          '& .MuiTableSortLabel-icon': {
                            marginLeft: '4px',
                          },
                        }}
                      >
                        <Box sx={{ color: '#64013c', textAlign: 'center' }}>
                          <Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                            Sign-Up Date
                          </Box>
                          <Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                            Time
                          </Box>
                        </Box>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.userRole}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.userRole)}
                      >
                        <div style={{ color: '#64013c' }}>{'Role'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.activeState}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.activeState)}
                      >
                        <div style={{ color: '#64013c' }}>{'Status'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <TableSortLabel
                        active={sortBy === SortBy.isApproved}
                        direction={sortByOrder === 'ASC' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort(SortBy.isApproved)}
                      >
                        <div style={{ color: '#64013c' }}>{'Approved'}</div>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align='center'>{'Reason'}</TableCell>
                    <TableCell align='center'>{'Edit'}</TableCell>
                    <TableCell align='center'>{'Action'}</TableCell>
                    <TableCell align='center'>{'License'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={10}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell align='center'>
                          {user?.name}
                          {/* {user?.firstName} {user?.lastName} */}
                        </TableCell>
                        <TableCell align='center'>{user?.email}</TableCell>
                        <TableCell align='center'>
                          {user?.countryCallingCode}-{user?.mobileNumber}
                        </TableCell>
                        <TableCell align='center'>
                          {user?.createdAt ? (
                            <Box sx={{ whiteSpace: 'nowrap' }}>
                              <Box component="span" sx={{ display: 'block' }}>
                                {fDateLine(user.createdAt)}
                              </Box>
                              <Box component="span" sx={{ display: 'block' }}>
                                {fTimeLine(user.createdAt)}
                              </Box>
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ textTransform: 'capitalize' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            {user?.userRole}
                            {user?.userRole === SuperUserRole.individual && (
                              <IconButton
                                aria-label='convert-role'
                                onClick={() => {
                                  setRoleConversionUser(user);
                                  onOpenRoleConversion();
                                }}
                                size="small"
                                color="primary"
                              >
                                <Iconify icon={'eva:edit-outline'} width={18} height={18} />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align='center'>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Label
                              variant='ghost'
                              color={user.activeState ? 'success' : 'error'}
                            >
                              {user.activeState ? 'Active' : 'InActive'}
                            </Label>
                            {!user.activeState && (user.userRole === 'company' || user.userRole === 'broker') && user.isSuspendedDueToLicenseExpiration && (
                              <Tooltip
                                title="Account is suspended because of license expiration"
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.5 }}
                                  aria-label='suspension-info'
                                >
                                  <Iconify icon={'eva:info-fill'} width={18} height={18} sx={{ color: 'info.main' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={user.isApproved ? 'success' : 'error'}
                          >
                            {user.isApproved ? 'Approved' : 'Not Approved'}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          {/* {user?.rejectionReason ? (
                              <>
                                {expandedRows[user.id]
                                  ? user?.rejectionReason
                                  : `${user?.rejectionReason.substring(0, 10)}...`}
                                <Button
                                  size='small'
                                  onClick={() => toggleExpand(user.id)}
                                  sx={{ marginLeft: 1, textTransform: 'none' }}
                                >
                                  {expandedRows[user.id]
                                    ? 'View Less'
                                    : 'View More'}
                                </Button>
                              </>
                            ) : (
                              '-'
                            )} */}
                          {user.rejectionReason ? (
                            <Tooltip
                              title={user.rejectionReason}
                              arrow
                            >
                              <span style={{ cursor: 'pointer' }}>
                                {user.rejectionReason.length > 10
                                  ? `${user.rejectionReason.substring(0, 10)}...`
                                  : user.rejectionReason}
                              </span>
                            </Tooltip>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton
                            aria-label='edit'
                            onClick={() => handleUpdate(user)}
                          >
                            <Iconify icon={'eva:edit-fill'} />
                          </IconButton>
                        </TableCell>
                        <TableCell align='center'>
                          {(user?.userRole === 'company' || user?.userRole === 'broker') &&
                            !user?.isApproved &&
                            user?.activeState ? (
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleActionUpdate(user)}
                            >
                              <Iconify icon={'eva:edit-outline'} />
                            </IconButton>
                          ) : (user?.userRole === 'company' || user?.userRole === 'broker') &&
                            user?.isApproved ? (
                            <IconButton
                              aria-label='view-details'
                              onClick={() => {
                                setActionUser(user);
                                onOpenDetails();
                              }}
                              color="primary"
                            >
                              <Iconify icon={'eva:eye-outline'} />
                            </IconButton>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {(user?.userRole === 'company' || user?.userRole === 'broker') &&
                            user?.isApproved ? (
                            <IconButton
                              aria-label='manage-license'
                              onClick={() => handleLicenseManagement(user)}
                              color='primary'
                            >
                              <Iconify icon={'eva:file-text-outline'} />
                            </IconButton>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <UsersFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
            getFieldProps={getFieldProps}
          />

          {toggleAction && (
            <UsersActionModal
              toggle={toggleAction}
              onClose={handleActionModalClose}
              actionUser={actionUser}
              search={search}
              sortBy={sortBy}
              sortByOrder={sortByOrder}
              onRefresh={handleRefreshUsers}
            />
          )}

          {toggleLicense && (
            <LicenseManagementModal
              toggle={toggleLicense}
              onClose={onCloseLicense}
              actionUser={actionUser}
              search={search}
              sortBy={sortBy}
              sortByOrder={sortByOrder}
              limit={limit}
              skip={skip}
            />
          )}

          {toggleDetails && (
            <CompanyDetailsModal
              toggle={toggleDetails}
              onClose={onCloseDetails}
              actionUser={actionUser}
            />
          )}

          {toggleRoleConversion && (
            <RoleConversionModal
              toggle={toggleRoleConversion}
              onClose={onCloseRoleConversion}
              actionUser={roleConversionUser}
              search={search}
              sortBy={sortBy}
              sortByOrder={sortByOrder}
            />
          )}
        </Container>
      </Page>
    </>
  );
};

export default Index;
